/**
 * Supabase Storage upload helpers for the V2 admin module.
 *
 * Uploads land in the existing `property-images` bucket (RLS: public read;
 * admins write anywhere, owners only under their own `owner-uploads/{uid}/`
 * prefix — see 20261030150000_col529_owner_property_image_uploads.sql). New
 * uploads use `owner-uploads/{uid}/{YYYY-MM-DD}/`.
 *
 * Validation:
 *  - MIME: image/jpeg | image/png | image/webp | image/avif
 *  - Max size: 10 MB per file
 *  - File extension is derived from the MIME (not the original filename)
 *    so a renamed `.jpg.exe` can't sneak through.
 */
import { supabase } from "@/integrations/supabase/client";

export const PROPERTY_IMAGES_BUCKET = "property-images";

const ALLOWED_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",
]);

// Documents (checklist proofs) additionally allow PDF.
const DOC_ALLOWED_MIME = new Set([...ALLOWED_MIME, "application/pdf"]);

const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/avif": "avif",
  "application/pdf": "pdf",
};

export const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

export type UploadValidation = { ok: boolean; reason: string | null };

function tooBig(file: File): UploadValidation | null {
  if (file.size > MAX_FILE_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    return {
      ok: false,
      reason: `File is ${mb} MB. Max upload size is 10 MB. Compress the file and try again.`,
    };
  }
  return null;
}

export function validateImageFile(file: File): UploadValidation {
  if (!ALLOWED_MIME.has(file.type)) {
    return {
      ok: false,
      reason: `Unsupported file type (${file.type || "unknown"}). Use JPG, PNG, WebP, or AVIF.`,
    };
  }
  return tooBig(file) ?? { ok: true, reason: null };
}

/** Checklist proofs: images plus PDF. */
export function validateDocumentFile(file: File): UploadValidation {
  if (!DOC_ALLOWED_MIME.has(file.type)) {
    return {
      ok: false,
      reason: `Unsupported file type (${file.type || "unknown"}). Use PDF, JPG, PNG, WebP, or AVIF.`,
    };
  }
  return tooBig(file) ?? { ok: true, reason: null };
}

/** Format Date as YYYY-MM-DD using UTC so the prefix is timezone-stable. */
function todayPrefix(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function randomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export type UploadResult = {
  url: string;
  path: string;
};

/**
 * The signed-in user's id, read from the LOCAL session — no network round-trip.
 *
 * `supabase.auth.getUser()` re-validates the token against the auth server on
 * every call; under a slow connection or a token refresh in flight it
 * intermittently errors and surfaced as a spurious "you must be signed in" on an
 * upload the user was perfectly authenticated for (worked on retry — COL-529,
 * Neel). `getSession()` returns the cached session (the client refreshes it in
 * the background), and storage RLS still enforces the real identity server-side,
 * so the session's uid is all these path builders need. Throws `signedOutMessage`
 * only when there is genuinely no session.
 */
async function currentUploaderId(signedOutMessage: string): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) throw new Error(signedOutMessage);
  return session.user.id;
}

/** Upload an already-validated file and resolve its public URL. */
async function uploadValidated(file: File): Promise<UploadResult> {
  // Owners aren't admins, and `property-images` is admin-write-only except under
  // `owner-uploads/{uid}/…` (COL-529 storage policy). Keying every upload by the
  // caller's uid satisfies that policy for owners and is allowed for admins too
  // (their policy is path-independent), so one path serves both.
  const uid = await currentUploaderId(
    "You must be signed in to upload images."
  );

  const ext = MIME_TO_EXT[file.type] ?? "bin";
  const path = `owner-uploads/${uid}/${todayPrefix()}/${randomId()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(PROPERTY_IMAGES_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      contentType: file.type,
      upsert: false,
    });

  if (uploadError) {
    throw new Error(`Upload failed: ${uploadError.message}`);
  }

  const { data } = supabase.storage
    .from(PROPERTY_IMAGES_BUCKET)
    .getPublicUrl(path);
  if (!data?.publicUrl) {
    throw new Error(
      "Upload succeeded but the public URL could not be resolved."
    );
  }
  return { url: data.publicUrl, path };
}

/**
 * Upload a single image to the property-images bucket.
 * Returns the public URL on success; throws on validation or upload error.
 */
export async function uploadPropertyImage(file: File): Promise<UploadResult> {
  const validation = validateImageFile(file);
  if (!validation.ok) {
    throw new Error(validation.reason ?? "Validation failed");
  }
  return uploadValidated(file);
}

/**
 * Upload a single checklist proof (PDF or image) to the property-images bucket.
 * Returns the public URL on success; throws on validation or upload error.
 */
export async function uploadPropertyDocument(
  file: File
): Promise<UploadResult> {
  const validation = validateDocumentFile(file);
  if (!validation.ok) {
    throw new Error(validation.reason ?? "Validation failed");
  }
  return uploadValidated(file);
}

/** Private bucket that holds owner/tenant documents (COL-436). */
export const CONTRACTS_BUCKET = "contracts";

/**
 * Upload a checklist proof to the caller's own owner-documents folder in the
 * private `contracts` bucket, and return the stored PATH (not a URL — the
 * bucket is private, so viewers must sign the path via `openStoredFile`).
 *
 * Why here and not `property-images`: that bucket is admin-write-only, so an
 * owner running the go-live checklist fails its RLS. The COL-551 storage policy
 * lets ANY authenticated caller write under `owner-documents/{their uid}/…`
 * (and admins write anywhere), which is exactly the checklist author. The
 * wizard has no entity id in scope yet, so the upload is keyed by the uploader;
 * the apartment_checklist_files row ties it to the unit at submit.
 */
export async function uploadChecklistDocument(file: File): Promise<string> {
  const validation = validateDocumentFile(file);
  if (!validation.ok) {
    throw new Error(validation.reason ?? "Validation failed");
  }

  const uid = await currentUploaderId(
    "You must be signed in to upload checklist documents."
  );

  const ext = MIME_TO_EXT[file.type] ?? "bin";
  const path = `owner-documents/${uid}/checklist/${todayPrefix()}/${randomId()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(CONTRACTS_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      contentType: file.type,
      upsert: false,
    });

  if (uploadError) {
    throw new Error(`Upload failed: ${uploadError.message}`);
  }

  return path;
}

/**
 * Upload an array of files in parallel with per-file outcome tracking.
 * Successful uploads return their URLs; failed ones return reasons.
 * Caller decides how to surface partial success (toast, list, etc.).
 */
export type ParallelUploadOutcome = {
  ok: boolean;
  file: File;
  url: string | null;
  path: string | null;
  reason: string | null;
};

export async function uploadPropertyImagesParallel(
  files: File[]
): Promise<ParallelUploadOutcome[]> {
  const settled = await Promise.allSettled(
    files.map(async (file) => {
      const r = await uploadPropertyImage(file);
      return { file, ...r };
    })
  );
  return settled.map((s, i): ParallelUploadOutcome => {
    if (s.status === "fulfilled") {
      return {
        ok: true,
        file: s.value.file,
        url: s.value.url,
        path: s.value.path,
        reason: null,
      };
    }
    const reason =
      s.reason instanceof Error ? s.reason.message : String(s.reason);
    return { ok: false, file: files[i], url: null, path: null, reason };
  });
}

/**
 * Best-effort delete of a previously-uploaded image. Used when the editor
 * removes an image from the wizard before submitting — keeps the bucket
 * tidy and avoids orphaned uploads. Failures are non-fatal.
 *
 * Pass either the full public URL or the storage path.
 */
export async function deletePropertyImageBestEffort(
  urlOrPath: string
): Promise<void> {
  let path = urlOrPath;
  // If it looks like a public URL, extract the path after the bucket marker
  const marker = `/storage/v1/object/public/${PROPERTY_IMAGES_BUCKET}/`;
  const idx = urlOrPath.indexOf(marker);
  if (idx !== -1) {
    path = decodeURIComponent(urlOrPath.slice(idx + marker.length));
  }
  // Only attempt if it looks like an upload path we own — never delete
  // legacy Storyblok URLs even if they were somehow piped through.
  // `owner-uploads/` is the current prefix (COL-529); `uploads/` is retained for
  // images uploaded before that change.
  if (!path.startsWith("owner-uploads/") && !path.startsWith("uploads/"))
    return;
  await supabase.storage
    .from(PROPERTY_IMAGES_BUCKET)
    .remove([path])
    .catch(() => {
      // Swallow — caller doesn't care
    });
}
